import React, { useEffect, useMemo, useState } from "react";
import "./MenuPage.css";
import logo from "../assets/logo.png";
import { FaInstagram, FaPhoneAlt, FaMapMarkerAlt } from "react-icons/fa";
import {
  getCategories,
  getPublicMenuItems,
  Category,
  PublicMenuItem,
} from "../supabase/menuService";

type MenuItem = PublicMenuItem;

const formatPrice = (value: number) =>
  new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY" }).format(
    value || 0
  );

const MenuPage: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCatId, setSelectedCatId] = useState<number | null>(null);

  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [loadingCats, setLoadingCats] = useState<boolean>(false);
  const [loadingItems, setLoadingItems] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const selectedCategory = useMemo(
    () => categories.find((c) => c.id === selectedCatId) || null,
    [categories, selectedCatId]
  );

  useEffect(() => {
    const fetchCategories = async () => {
      setLoadingCats(true);
      setError(null);
      try {
        const cats: Category[] = await getCategories();
        setCategories(cats);
        if (cats.length) setSelectedCatId(cats[0].id);
      } catch (e) {
        setError("Kategorileri yüklerken bir hata oluştu.");
        console.error(e);
      } finally {
        setLoadingCats(false);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    if (selectedCatId === null) return;
    let isMounted = true;

    const fetchItems = async () => {
      setLoadingItems(true);
      setError(null);
      try {
        const items: MenuItem[] = await getPublicMenuItems(selectedCatId);

        if (isMounted) setMenuItems(items);
      } catch (e) {
        setError("Menü öğelerini yüklerken bir hata oluştu.");
        console.error(e);
      } finally {
        if (isMounted) setLoadingItems(false);
      }
    };

    fetchItems();
    return () => {
      isMounted = false;
    };
  }, [selectedCatId]);

  return (
    <div className="menu-page">
      <div className="main-card" role="main">
        {/* Header */}
        <header className="header">
          <img src={logo} alt="Kevser's Coffee" className="coffee-logo" />
          <h1 className="brand-name">Kevser's Coffee</h1>
        </header>

        <section className="welcome-section">
          <h2 className="welcome-title">Hoş Geldiniz</h2>
          <p className="welcome-text">
            Taptaze kahvelerimiz ve lezzetli atıştırmalıklarımız: Ön menümüzü
            aşağıdan inceleyebilirsiniz.
          </p>
        </section>

        <nav className="category-filter" aria-label="Menü kategorileri">
          {loadingCats && (
            <span className="status-text">Kategoriler yükleniyor…</span>
          )}
          {!loadingCats &&
            categories.map((cat) => (
              <button
                key={cat.id}
                className={`category-btn ${
                  selectedCatId === cat.id ? "active" : ""
                }`}
                onClick={() => setSelectedCatId(cat.id)}
                aria-pressed={selectedCatId === cat.id}
              >
                {cat.name}
              </button>
            ))}
        </nav>

        <section className="menu-list-container">
          <h2 className="menu-category-title">
            {selectedCategory ? selectedCategory.name : "Menü"}
          </h2>
          <hr />

          {error && <p className="status-text error">{error}</p>}
          {loadingItems && <p className="status-text">Yükleniyor...</p>}
          {!loadingItems && !error && menuItems.length === 0 && (
            <p className="status-text">Bu kategoride ürün yok.</p>
          )}

          <div className="menu-items-list">
            {[...menuItems]
              .sort((a, b) => {
                const aHasImage = Boolean(a.image);
                const bHasImage = Boolean(b.image);
                if (aHasImage === bHasImage) return 0;
                return aHasImage ? -1 : 1;
              })
              .map((item) => {
                const hasImage = Boolean(item.image);
                return (
                  <article key={item.id} className="menu-item-card">
                    {hasImage && (
                      <div className="menu-item-image-wrapper">
                        <img
                          src={item.image}
                          alt={item.title}
                          className="menu-item-image"
                          loading="lazy"
                          sizes="(max-width: 600px) 100vw, 600px"
                        />
                      </div>
                    )}

                    <div className="menu-item-details">
                      <h3 className="menu-item-title">{item.title}</h3>

                      {!!item.description && (
                        <p className="menu-item-description">
                          {item.description}
                        </p>
                      )}

                      {!!item.extras?.length && (
                        <div className="menu-item-extras">
                          <p className="extras-title">Ekstralar:</p>
                          <ul className="extras-list">
                            {item.extras.map((name) => (
                              <li key={name} className="extra-chip">
                                {name}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      <p className="menu-item-price" aria-label="Fiyat">
                        {formatPrice(item.price)}
                      </p>
                    </div>
                  </article>
                );
              })}
          </div>
        </section>

        <section className="contact-section">
          <h2 className="contact-title">İletişim</h2>
          <div className="contact-info">
            <div className="contact-item">
              <a
                href="https://www.instagram.com/kevserscafe?igsh=MWEzNjNzdTlsa2RyNg=="
                className="social-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FaInstagram className="icon" aria-hidden />
                <span>Instagram: @kevserscafe</span>
              </a>
            </div>

            <div className="contact-item">
              <FaPhoneAlt className="icon" aria-hidden />
              <a
                href="https://wa.me/905424387281"
                target="_blank"
                rel="noopener noreferrer"
                className="contact-link"
              >
                0542 438 7281
              </a>
            </div>

            <div className="contact-item">
              <FaMapMarkerAlt className="icon" aria-hidden />
              <a
                href="https://www.google.com/maps?q=Esenkent+Mah.+Atatürk+Cad.+Hayrat+Sk.+No:+2B"
                target="_blank"
                rel="noopener noreferrer"
                className="contact-link"
              >
                Esenkent Mah. Atatürk Cad. Hayrat Sk. No: 2B
              </a>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default MenuPage;
